import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import * as api from '@/apis/aipass'
import { SettingSalesManagerTab } from '@/components/organisms/settings/sales-manager/tab'
import type { AccountsReceivableSettingType } from '@/models/accounts-receivable'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { Button } from '@/components/atoms'
import { EditFooter } from '@/components/organisms/edit-footer'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { SettingDiscountARRuleList } from '../../../organisms/settings/sales-manager/accounts-receivable-display/list'
import type { DiscountARRule } from '@/models/discount-ar-rule'
import type { DiscountSettingType } from '@/models/sales'
import { SettingDiscountARRuleDetailModal } from '../../../organisms/settings/sales-manager/accounts-receivable-display/detail-modal'

export type DiscountARRuleMaster = {
  discounts: DiscountSettingType[]
  accountsReceivables: AccountsReceivableSettingType[]
}

export const AccountsReceivableDisplay: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()

  const [discountARRules, setDiscountARRules] = useState<DiscountARRule[]>([])
  const [discountARRuleMaster, setDiscountARRuleMaster] = useState<DiscountARRuleMaster>({ discounts: [], accountsReceivables: [] })
  const [deleteIds, setDeleteIds] = useState<string[]>([])
  const [editingRule, setEditingRule] = useState<DiscountARRule | null>()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const initializeComponent = async () => {
    setIsLoading(true)
    try {
      const [rules, discounts, accountsReceivables] = await Promise.all([
        api.fetchSalesDiscountARRules(),
        api.fetchSalesDiscount(),
        api.fetchSalesAccountsReceivable(),
      ])
      setDiscountARRules(rules)
      setDiscountARRuleMaster({
        discounts: discounts?.salesDiscount || [],
        accountsReceivables: accountsReceivables?.accountsReceivables || [],
      })
      setDeleteIds([])
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const addDeleteTarget = (id: string) => {
    setDeleteIds(currentDeleteIds => [...currentDeleteIds, id])
  }

  const doDelete = async () => {
    try {
      setIsLoading(true)
      if (deleteIds.length) {
        await api.deleteSalesDiscountARRules(deleteIds)
        await initializeComponent()
      }
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    initializeComponent()
  }, [])

  const displayRules = useMemo(() => discountARRules.filter(rule => !deleteIds.includes(rule.id)), [discountARRules, deleteIds])
  const footerContent = deleteIds.length ? <EditFooter onSave={doDelete} onCancel={() => setDeleteIds([])} /> : undefined

  return (
    <SettingsLayout loading={isLoading} footerContent={footerContent}>
      <TabContainer tabComponent={<SettingSalesManagerTab currentTab="AR Display" />}>
        <div css={containerHeaderStyle}>
          <Button
            buttonType={1}
            width={107}
            height={32}
            fontSize={12}
            icon={require('@/static/images/plus-white.svg')}
            onClick={() => setEditingRule(null)}
          >
            {t('Add new')}
          </Button>
          <div className="notice">
            売掛金管理画面の表示対象を設定できます。未登録の場合、割引科目で売掛として登録されている割引科目は売掛金対象となり、売掛で登録されていない割引科目は対象外となります。
          </div>
        </div>
        <SettingDiscountARRuleList rules={displayRules} master={discountARRuleMaster} onDelete={addDeleteTarget} onEdit={setEditingRule} />
        {editingRule !== undefined && (
          <SettingDiscountARRuleDetailModal
            detail={editingRule}
            master={discountARRuleMaster}
            existingRules={discountARRules}
            onCancel={() => setEditingRule(undefined)}
            onSave={() => {
              setEditingRule(undefined)
              initializeComponent()
            }}
          />
        )}
      </TabContainer>
    </SettingsLayout>
  )
}

const containerHeaderStyle = css({
  display: 'flex',
  alignItems: 'flex-start',
  flexDirection: 'column',
  gap: 16,
  padding: '24px 32px 19px 32px',
  borderBottom: '1px solid #F2F2F2',
  '.notice': {
    fontSize: 12,
  },
})
